import { Button, LoadingScreen, Text } from '@cabonline/ui-web';
import { ErrorText } from '@cabonline/ui-web';
import { colors, getBorderRadius, getSpacing } from '@cabonline/ui-shared';
import styled from '@emotion/styled';
import { InfoOutlined } from '@mui/icons-material';
import { useAuth, useLocale } from '@multiplatform-apps/service-trips';
import { Layout } from '../../components';
import { LayoutProvider } from '../../context';
import { getCurityRedirectUri } from '../../utils';
import { encrypt } from '../../utils/encrypt';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { paths } from '../../constants';
import { useDeviceType } from '../../hooks';
import {
  StyledContainer,
  StyledRight,
  StyledWelcomeContainer,
  StyledWelcomeText,
} from './styles';

const StyledInfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${getSpacing('xs')};
  padding: ${getSpacing('xs')} ${getSpacing('s')};
  background-color: ${colors.blueLight};
  border: 1px solid ${colors.blue};
  border-radius: ${getBorderRadius('s')};
  margin-bottom: ${getSpacing('s')};
`;

const SESSION_EXPIRED_MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes

const getSessionExpired = (): boolean => {
  const raw = localStorage.getItem('session_expired_at');
  if (!raw) return false;
  const timestamp = Number(raw);
  if (Number.isNaN(timestamp)) {
    localStorage.removeItem('session_expired_at');
    return false;
  }
  const age = Date.now() - timestamp;
  if (age > SESSION_EXPIRED_MAX_AGE_MS) {
    localStorage.removeItem('session_expired_at');
    return false;
  }
  return true;
};

export const LandingPage: React.FC = () => {
  const [showApp, setShowApp] = useState(false);
  const [sessionExpired] = useState(getSessionExpired);
  const navigate = useNavigate();
  const { isAuthenticated, generateLoginUri } = useAuth();

  const deviceType = useDeviceType();
  const { t } = useLocale();

  const [params] = useSearchParams();

  const error = params.get('error');
  const autostart = params.get('autostart');

  const start = useCallback(() => {
    localStorage.removeItem('session_expired_at');
    const redirectUri = getCurityRedirectUri();
    generateLoginUri(encrypt, redirectUri)
      .then((url) => {
        window.location.href = url;
      })
      .catch((e) => {
        navigate(`${paths.landingPage}?error=auth_error`);
      });
  }, [generateLoginUri, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      setShowApp(true);
      navigate(paths.root);
      return;
    }

    if (autostart === 'true') {
      start();
    } else {
      setShowApp(true);
    }
  }, [autostart, isAuthenticated, navigate, params, start]);

  return showApp ? (
    <LayoutProvider>
      <Layout removePadding>
        <StyledContainer>
          <StyledWelcomeContainer>
            <StyledWelcomeText>
              {sessionExpired && (
                <StyledInfoBanner role="status">
                  <InfoOutlined fontSize="small" htmlColor={colors.blue} aria-hidden="true" />
                  <Text size="s" color={colors.blue}>
                    {t('landingPage.sessionExpired')}
                  </Text>
                </StyledInfoBanner>
              )}
              <Text as="h1" size="xxl" marginBottom="s" weight="extraBold">
                {t('landingPage.title')}
              </Text>
              <Text size="l" marginBottom="s">
                {t('landingPage.description')}
              </Text>
              {error && <ErrorText marginBottom="xs">{t('landingPage.loginError')}</ErrorText>}
              <Button
                testID="signin-button"
                label={t('landingPage.loginButton')}
                onClick={start}
                width="180px"
              />
            </StyledWelcomeText>
          </StyledWelcomeContainer>
          {deviceType !== 'mobile' && <StyledRight />}
        </StyledContainer>
      </Layout>
    </LayoutProvider>
  ) : (
    <LoadingScreen minHeight="100vh" />
  );
};
